import AppConstants from '../constants/AppConstants';
import translations from '../locales.js';
import store from './redux';

const {
    ROLE_PUBLISHER,
    ROLE_ADMIN,
    ROLE_SUPER_ADMIN,
    ROLE_AFFILIATE_MANAGER,
    ROLE_SUPPORT,
    ROLE_FINANCIER,
    ROLE_ADVERTISER,
} = AppConstants;

let impersonating = '';

const safeGetState = () => {
    try {
        return store?.getState?.() || {};
    } catch {
        return {};
    }
};

const getUserSlice = () => safeGetState().user || {};

const normalizeToArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (val instanceof Set) return Array.from(val);
    return [val];
};

const getRoles = () => normalizeToArray(getUserSlice().roles);
const defaultDict = () => translations.en || Object.values(translations)[0] || {};

const tzFallback = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
};

const UserStore = {
    /** @returns {boolean} */
    getAccess() {
        const roles = getRoles();
        return roles.includes(ROLE_PUBLISHER) || roles.length === 0;
    },

    /** @returns {string} */
    getTimeZone() {
        return getUserSlice().timezone || tzFallback();
    },

    /** @returns {string} */
    getLocale() {
        return getUserSlice().locale || 'en';
    },

    /** @returns {Object} */
    getTranslation() {
        const loc = this.getLocale();
        return translations[loc] || defaultDict();
    },

    /** @returns {number|string|null} */
    getID() {
        const id = getUserSlice().id;
        return typeof id === 'number' || typeof id === 'string' ? id : null;
    },

    /** @returns {string} */
    getImpersonating() {
        return impersonating;
    },
    /** @param {string} value */
    setImpersonating(value) {
        impersonating = value;
    },
    /** @returns {boolean} */
    isImpersonatingActive() {
        return Boolean(impersonating);
    },

    /** @returns {boolean} */
    isAdmin() {
        const roles = getRoles();
        return (
            roles.includes(ROLE_ADMIN) ||
            roles.includes(ROLE_SUPER_ADMIN) ||
            roles.includes(ROLE_AFFILIATE_MANAGER) ||
            roles.includes(ROLE_SUPPORT) ||
            roles.includes(ROLE_FINANCIER)
        );
    },

    /** @returns {boolean} */
    isAdvertiser() {
        const roles = getRoles();
        return (
            roles.includes(ROLE_ADMIN) ||
            roles.includes(ROLE_SUPER_ADMIN) ||
            roles.includes(ROLE_ADVERTISER)
        );
    },

    getRoles,
};

export default UserStore;
